import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PORTFOLIO_CATEGORIES, PORTFOLIO_PILLARS } from "../../data/content";

const Marquee = () => (
  <Reveal>
    <div className="mt-12 overflow-hidden border-y border-[#c9a24d]/20 py-3.5">
      <div className="portfolio-marquee-track flex w-max items-center">
        {[0, 1, 2].map((rep) => (
          <div key={rep} className="flex shrink-0 items-center">
            {PORTFOLIO_PILLARS.map((pillar, i) => (
              <div key={`${rep}-${i}`} className="flex shrink-0 items-center">
                <span className="font-hero whitespace-nowrap px-[22px] text-[15px] italic text-neutral-400">
                  {pillar.toUpperCase()}
                </span>
                <span className="flex items-center text-3xl leading-none text-[#c9a24d]">&bull;</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </Reveal>
);

const CategoryRow = ({ cat, index, onEnter, onOpen }) => (
  <Reveal delay={index * 0.06} y={16}>
    <div
      role="button"
      tabIndex={0}
      data-testid={`portfolio-row-${cat.slug}`}
      onMouseEnter={() => onEnter(index)}
      onClick={() => onOpen(cat)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(cat)}
      style={{ borderLeftColor: `${cat.color}55` }}
      className="group grid cursor-pointer grid-cols-[36px_1fr_28px] items-center gap-4 border-b border-[#c9a24d]/15 border-l-2 py-[34px] pl-[6px] pr-[6px] transition-[padding-left,border-left-color] duration-300 ease-out first:border-t sm:grid-cols-[64px_1fr_auto_auto_40px] sm:gap-7 hover:pl-[18px]"
    >
      <span className="font-mono text-[11px] tracking-widest opacity-85 sm:text-xs" style={{ color: cat.color }}>
        {String(index + 1).padStart(2, "0")}
      </span>
      <h3 className="font-hero text-[26px] font-semibold leading-[1.1] text-[#f3efe7] sm:text-[34px]">
        {cat.title}
      </h3>
      <span
        className="hidden text-[11px] font-bold uppercase tracking-[0.15em] sm:inline sm:whitespace-nowrap"
        style={{ color: cat.color }}
      >
        {cat.subtitle}
      </span>
      <span className="hidden font-mono text-xs text-white/35 sm:inline sm:whitespace-nowrap">
        {cat.images.length} Photos
      </span>
      <span className="flex items-center justify-end text-lg text-[#c9a24d] opacity-0 transition-all duration-300 -translate-x-1.5 group-hover:translate-x-0 group-hover:opacity-100">
        &rarr;
      </span>
    </div>
  </Reveal>
);

export const Projects = () => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [activeCat, setActiveCat] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const listRef = useRef(null);

  const handleMove = (e) => {
    if (!listRef.current) return;
    const rect = listRef.current.getBoundingClientRect();
    setMouse({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const openGallery = (cat) => {
    setActiveCat(cat);
    setLightboxIdx(null);
  };

  const closeGallery = () => {
    setActiveCat(null);
    setLightboxIdx(null);
  };

  useEffect(() => {
    if (lightboxIdx === null || !activeCat) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") {
        setLightboxIdx((i) => (i + 1) % activeCat.images.length);
      }
      if (e.key === "ArrowLeft") {
        setLightboxIdx((i) => (i - 1 + activeCat.images.length) % activeCat.images.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, activeCat]);

  const hoveredCat = hoverIdx !== null ? PORTFOLIO_CATEGORIES[hoverIdx] : null;

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative border-y border-[#c9a24d]/25 bg-[#0a0a0a] pb-[60px] pt-[100px]"
    >
      <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-10">
            <div>
              <span className="mb-[18px] block text-xs font-bold uppercase tracking-[0.3em] text-[#c9a24d]">
                Our Portfolio
              </span>
              <h2 className="font-hero max-w-xl text-[40px] font-semibold leading-[1.2] text-white sm:text-[52px]">
                Craft, poured in concrete.
              </h2>
            </div>
            <p className="max-w-[340px] text-[14.5px] leading-relaxed text-white/55">
              A collection of work that defines the Blacklion standard across
              Toronto and the GTA. Hover a title to preview the build.
            </p>
          </div>
        </Reveal>

        <Marquee />

        <div
          ref={listRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
          className="relative mt-9"
        >
          <div
            className="portfolio-hint pointer-events-none absolute left-1/2 top-[-18px] flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 text-sm font-medium tracking-wide text-[#f3efe7] transition-opacity duration-400 ease-out"
            style={{ opacity: hoverIdx !== null ? 0 : 1 }}
          >
            <span>hover a title</span>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M5 3l14 8-6 1.5L11 19 5 3z" fill="#f3efe7" />
            </svg>
          </div>

          {PORTFOLIO_CATEGORIES.map((cat, i) => (
            <CategoryRow key={cat.slug} cat={cat} index={i} onEnter={setHoverIdx} onOpen={openGallery} />
          ))}

          <div
            className="pointer-events-none absolute left-0 top-0 z-10 hidden h-[230px] w-[340px] overflow-hidden border border-[#c9a24d]/40 lg:block"
            style={{
              transform: `translate(${mouse.x - 170}px, ${mouse.y - 160}px) rotate(${hoveredCat ? -2 : 0}deg) scale(${hoveredCat ? 1 : 0.85})`,
              opacity: hoveredCat ? 1 : 0,
              transition: hoveredCat ? "opacity .25s ease, transform .25s ease" : "opacity .2s ease",
            }}
          >
            {hoveredCat && (
              <>
                <img src={hoveredCat.cover} alt="" className="h-full w-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#c9a24d]/80">
                  {hoveredCat.title} &mdash; {hoveredCat.images.length} Photos
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!activeCat} onOpenChange={(open) => !open && closeGallery()}>
        <DialogContent className="max-h-[88vh] w-[94vw] max-w-6xl gap-0 overflow-y-auto border-[#c9a24d]/20 bg-[#0e0d0c] p-0 sm:rounded-none">
          {activeCat && (
            <>
              <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-[#c9a24d]/15 bg-[#0e0d0c] py-5 pl-6 pr-14 sm:pl-10 sm:pr-16">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#c9a24d]">
                    {activeCat.subtitle}
                  </span>
                  <DialogTitle className="font-hero mt-1 text-xl font-semibold tracking-normal text-white sm:text-2xl">
                    {activeCat.title} &mdash; Full Gallery
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Full photo gallery for the {activeCat.title} portfolio category.
                  </DialogDescription>
                </div>
                <span className="shrink-0 font-mono text-xs text-white/40">
                  {activeCat.images.length} Photos
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 p-6 sm:grid-cols-3 sm:gap-3 sm:p-10">
                {activeCat.images.map((img, i) => (
                  <button
                    key={img.src}
                    type="button"
                    onClick={() => setLightboxIdx(i)}
                    data-testid={`gallery-thumb-${activeCat.slug}-${i}`}
                    className="group relative aspect-[4/3] overflow-hidden"
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 border border-transparent transition-colors duration-300 group-hover:border-[#c9a24d]/50" />
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {activeCat && lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95 p-4 sm:p-10"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx(null);
            }}
            className="absolute right-5 top-5 text-white/70 transition-colors hover:text-white"
          >
            <X size={28} />
          </button>
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx((i) => (i - 1 + activeCat.images.length) % activeCat.images.length);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 transition-colors hover:text-[#c9a24d] sm:left-8"
          >
            <ChevronLeft size={36} />
          </button>
          <img
            src={activeCat.images[lightboxIdx].src}
            alt={activeCat.images[lightboxIdx].alt}
            className="max-h-[85vh] max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx((i) => (i + 1) % activeCat.images.length);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 transition-colors hover:text-[#c9a24d] sm:right-8"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </section>
  );
};
